import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Collapse,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { Button } from '../../../../components/ui';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { formatDurationSeconds, getAgentResponseBenchmark } from '../../utils/interveneFilters';
import { analyticsService } from '../../services/analyticsService';

const NESTED_HEAD_CELL = {
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#334155 !important',
  textAlign: 'center !important',
  py: '8px !important',
  px: '8px !important',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #e2e8f0',
};

function maskPhoneNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length < 8) {
    return value || '—';
  }
  return `${digits.slice(0, 4)}…${digits.slice(-4)}`;
}

function parseSessionDate(value) {
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T/.test(value) &&
    !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
  ) {
    // Analytics stores naive datetimes as UTC; make that explicit before parsing.
    return new Date(`${value}Z`);
  }
  return new Date(value);
}

function formatSessionDate(val) {
  if (!val) return '—';

  const d = parseSessionDate(val);
  if (!Number.isNaN(d.getTime())) {
    const formatted = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(d);

    return formatted.replace(/\s*([ap]\.?m\.?)$/i, (m, p1) => ` ${p1.replace(/\./g, '').toUpperCase()}`);
  }

  return String(val);
}

function extractDateOnly(session) {
  const raw = session?.started_at || session?.Session;

  if (!raw) return 'Unknown Date';

  if (typeof raw === 'string' && raw.includes('T')) {
    try {
      const d = parseSessionDate(raw);

      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', {
          timeZone: 'Asia/Kolkata',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      }
    } catch {
      // ignore
    }
  }

  if (typeof raw === 'string') {
    const parts = raw.split(',');

    if (parts.length > 0) {
      return parts[0].trim();
    }
  }

  return String(raw);
}

function ReplyBreakdownRow({ reply, index }) {
  const seconds =
    typeof reply.response_seconds === 'number'
      ? reply.response_seconds
      : null;

  const benchmark =
    seconds != null ? getAgentResponseBenchmark(seconds) : null;

  const displayTime = reply.is_pending
    ? ''
    : seconds != null
      ? formatDurationSeconds(seconds)
      : reply.response_display || '—';

  const customerLastReplyAt =
    reply.customer_last_reply_at ||
    reply.customer_reply_at ||
    reply['Customer last reply'];
  const customerLastReply = customerLastReplyAt
    ? formatSessionDate(customerLastReplyAt)
    : '—';

  return (
    <TableRow
      sx={{
        '&:last-child td': { border: 0 },
        bgcolor: '#f8fafc',
      }}
    >
      <TableCell
        sx={{
          py: 0.5,
          px: 2,
          fontSize: '0.75rem',
          color: '#64748b',
        }}
      >
        Reply #{index + 1}
      </TableCell>

      {/* Customer Last Reply */}
      <TableCell
        sx={{
          py: 0.5,
          px: 2,
          fontSize: '0.75rem',
          color: '#334155',
          whiteSpace: 'nowrap',
        }}
      >
        {customerLastReply}
      </TableCell>

      {/* Replied At - includes seconds */}
      <TableCell
        sx={{
          py: 0.5,
          px: 2,
          fontSize: '0.75rem',
          color: '#334155',
          whiteSpace: 'nowrap',
        }}
      >
        {reply.is_pending
          ? 'No reply by agent'
          : formatSessionDate(reply.reply_at || reply.time)}
      </TableCell>

      <TableCell
        sx={{
          py: 0.5,
          px: 2,
          fontSize: '0.75rem',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            justifyContent: 'center',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {displayTime}
          </Typography>

          {benchmark && (
            <Chip
              size="small"
              label={benchmark.label}
              sx={{
                fontSize: '0.625rem',
                fontWeight: 700,
                color: benchmark.color,
                bgcolor: benchmark.bg,
                height: 18,
              }}
            />
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function SessionsTableHead() {
  return (
    <TableHead
      sx={{
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      <TableRow>
        <TableCell sx={{ ...NESTED_HEAD_CELL, textAlign: 'left !important' }}>
          Customer
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Started
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Session End
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Last Activity
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Replies
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Avg Response
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          First Reply
        </TableCell>

        <TableCell sx={NESTED_HEAD_CELL}>
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function SessionRow({ session, querySpec, orgId, replyPageSize = 20 }) {
  const [open, setOpen] = useState(false);
  const [loadedReplies, setLoadedReplies] = useState(null);
  const [replyCursor, setReplyCursor] = useState(null);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [replyError, setReplyError] = useState('');

  const replies = Array.isArray(session.reply_details)
    ? session.reply_details
    : (loadedReplies || []);

  const replyCount = Number(session.reply_count ?? session.Replies ?? replies.length) || 0;

  const loadReplies = async (cursor = null) => {
    if (isLoadingReplies || !session.session_id) return;
    setIsLoadingReplies(true);
    setReplyError('');
    try {
      const response = await analyticsService.getInterveneSessionReplies(
        querySpec,
        { sessionId: session.session_id, limit: replyPageSize, cursor },
        orgId
      );
      if (!response?.ok) {
        throw new Error(response?.data?.message || response?.data || 'Could not load reply details.');
      }
      const page = response.data || {};
      setLoadedReplies((previous) => cursor
        ? [...(previous || []), ...(page.replies || [])]
        : (page.replies || []));
      setReplyCursor(page.next_cursor || null);
      setHasMoreReplies(Boolean(page.has_more));
    } catch (error) {
      setReplyError(error?.message || 'Could not load reply details.');
    } finally {
      setIsLoadingReplies(false);
    }
  };

  const toggleReplies = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (
      nextOpen
      && !Array.isArray(session.reply_details)
      && loadedReplies === null
      && !isLoadingReplies
    ) {
      loadReplies();
    }
  };

  const customer =
    session.Customer ||
    session.phone_number ||
    '—';

  /*
   * Started
   * Seconds are included through formatSessionDate().
   */
  const sessionTime =
    session.started_at
      ? formatSessionDate(session.started_at)
      : session.Session
      ? formatSessionDate(session.Session)
      : '—';

  /*
   * Session End
   * Seconds are included through formatSessionDate().
   */
  const sessionEndRaw =
    session.session_end_at ||
    session.session_end_display_at;
  const sessionEndLabel = session['Session end'];
  const sessionEndTime = sessionEndRaw
    ? formatSessionDate(sessionEndRaw)
    : session.session_end_in_progress ||
        (typeof sessionEndLabel === 'string' && sessionEndLabel.toLowerCase().includes('progress'))
      ? 'In progress'
      : sessionEndLabel
        ? formatSessionDate(sessionEndLabel)
        : '—';

  /*
   * Last Activity
   * Seconds are included through formatSessionDate().
   */
  const lastActive =
    session.last_activity_at || session.ended_at
      ? formatSessionDate(
          session.last_activity_at || session.ended_at
        )
      : session['Last activity']
      ? formatSessionDate(session['Last activity'])
      : '—';

  const avgResponse = session.avg_seconds != null && Number.isFinite(Number(session.avg_seconds))
    ? formatDurationSeconds(session.avg_seconds)
    : session['Avg (is chat)'] || session.avg_display || '—';

  const firstReply = session.first_reply_seconds != null && Number.isFinite(Number(session.first_reply_seconds))
    ? formatDurationSeconds(session.first_reply_seconds)
    : session['First reply'] || session.first_reply_display || '—';

  const hasReplies = replyCount > 0
    || Number(session.pending_reply_count) > 0
    || replies.length > 0;

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          '& > *': {
            borderBottom: open
              ? 'none'
              : '1px solid #e2e8f0',
          },
          bgcolor: open
            ? '#f0fdf4'
            : '#ffffff',
        }}
      >
        {/* Customer */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            textAlign: 'center !important',
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
            }}
          >
            <PhoneIcon
              sx={{
                fontSize: 15,
                color: '#059669',
              }}
            />

            <Tooltip title={customer} arrow>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#0f172a',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {maskPhoneNumber(customer)}
              </Typography>
            </Tooltip>
          </Box>
        </TableCell>

        {/* Started */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            color: '#475569',
            textAlign: 'center !important',
          }}
        >
          {sessionTime}
        </TableCell>

        {/* Session End */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            color: '#475569',
            textAlign: 'center !important',
          }}
        >
          {sessionEndTime}
        </TableCell>

        {/* Last Activity */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            color: '#475569',
            textAlign: 'center !important',
          }}
        >
          {lastActive}
        </TableCell>

        {/* Replies */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            textAlign: 'center !important',
            whiteSpace: 'nowrap',
          }}
        >
          <Chip
            size="small"
            label={`${replyCount} ${
              replyCount === 1
                ? 'reply'
                : 'replies'
            }`}
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor:
                replyCount > 0
                  ? '#dcfce7'
                  : '#f1f5f9',
              color:
                replyCount > 0
                  ? '#15803d'
                  : '#64748b',
              height: 20,
            }}
          />
        </TableCell>

        {/* Avg Response */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            textAlign: 'center !important',
            fontWeight: 600,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          }}
        >
          {avgResponse}
        </TableCell>

        {/* First Reply */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            textAlign: 'center !important',
            fontWeight: 600,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
          }}
        >
          {firstReply}
        </TableCell>

        {/* Actions */}
        <TableCell
          sx={{
            py: '8px !important',
            px: '8px !important',
            textAlign: 'center !important',
            whiteSpace: 'nowrap',
          }}
        >
          {hasReplies ? (
            <Tooltip
              title={
                open
                  ? 'Hide Line Details Table'
                  : 'Click to view Line Details Table'
              }
              arrow
            >
              <Box
                component="button"
                type="button"
                onClick={toggleReplies}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.6,
                  px: 1,
                  py: 0.35,
                  border: '1px solid',
                  borderColor: open
                    ? '#059669'
                    : 'rgba(5, 150, 105, 0.35)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  bgcolor: open
                    ? '#dcfce7'
                    : 'rgba(5, 150, 105, 0.08)',
                  color: open ? '#15803d' : '#059669',
                  font: 'inherit',
                  fontWeight: 600,
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    bgcolor: open
                      ? '#bbf7d0'
                      : 'rgba(5, 150, 105, 0.18)',
                    borderColor: '#059669',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    bgcolor: open ? '#15803d' : '#059669',
                    color: '#ffffff',
                    px: 0.55,
                    py: 0.1,
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {replyCount}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: open ? '#15803d' : '#047857',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {open
                    ? 'Hide Line Details'
                    : 'View Line Details'}
                </Typography>
                {open ? (
                  <KeyboardArrowUpIcon
                    sx={{ fontSize: 15 }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 15 }}
                  />
                )}
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title={ANALYTICS_COPY.interveneBenchmarkNoLineDetails} arrow>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: '#94a3b8',
                  textAlign: 'center',
                  cursor: 'help',
                }}
              >
                {ANALYTICS_COPY.interveneBenchmarkNoReplyChip}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {hasReplies && (
        <TableRow sx={{ bgcolor: '#f8fafc' }}>
          <TableCell
            colSpan={8}
            sx={{
              py: 0,
              px: 2,
              borderBottom:
                '1px solid #e2e8f0',
            }}
          >
            <Collapse
              in={open}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  my: 1,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: '#ffffff',
                  border:
                    '1px solid #e2e8f0',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    mb: 0.75,
                    color: '#334155',
                  }}
                >
                  Line Details (
                  {customer} · {sessionTime}
                  )
                </Typography>

                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: '#f1f5f9',
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 2,
                          fontWeight: 700,
                          fontSize: '0.6875rem',
                        }}
                      >
                        Sequence
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 2,
                          fontWeight: 700,
                          fontSize: '0.6875rem',
                        }}
                      >
                        Customer Last Reply
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 2,
                          fontWeight: 700,
                          fontSize: '0.6875rem',
                        }}
                      >
                        Replied At
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 2,
                          fontWeight: 700,
                          fontSize: '0.6875rem',
                          textAlign: 'center',
                        }}
                      >
                        Response Time
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoadingReplies && replies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    ) : replyError ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Alert severity="error">{replyError}</Alert>
                        </TableCell>
                      </TableRow>
                    ) : replies.map((reply, idx) => (
                      <ReplyBreakdownRow
                        key={reply.reply_msg_id || `${reply.reply_at || reply.time}-${idx}`}
                        reply={reply}
                        index={idx}
                        session={session}
                      />
                    ))}
                  </TableBody>
                </Table>
                {hasMoreReplies && !Array.isArray(session.reply_details) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button
                      variant="outline"
                      disabled={isLoadingReplies}
                      onClick={() => loadReplies(replyCursor)}
                    >
                      {isLoadingReplies ? 'Loading…' : 'Load more replies'}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

export default function AgentSessionsRowPanel({
  agentName = 'Agent',
  sessions = [],
  row = null,
  chats = null,
  sessionsStarted = null,
  isLoadingSessions = false,
  sessionsError = '',
  hasMoreSessions = false,
  onLoadMoreSessions,
  querySpec = null,
  orgId = '',
  replyPageSize = 20,
}) {
  const [search, setSearch] =
    useState('');

  const [replyFilter, setReplyFilter] =
    useState('all');

  const [groupMode, setGroupMode] =
    useState('customer');

  const [sortBy, setSortBy] =
    useState('latest');

  const totalSessionsCount =
    sessionsStarted ??
    row?.['Sessions started'] ??
    row?.['Number of sessions'] ??
    row?.sessions_started ??
    sessions.length;

  // Summary statistics
  const stats = useMemo(() => {
    const total = totalSessionsCount;

    let withReplies = 0;

    const uniqueCustomers =
      new Set();

    sessions.forEach((s) => {
      const count =
        s.reply_count ??
        s.Replies ??
        0;

      if (count > 0) {
        withReplies++;
      }

      const cust =
        s.Customer ||
        s.phone_number;

      if (cust) {
        uniqueCustomers.add(cust);
      }
    });

    return {
      total,
      withReplies,
      noReplies:
        Math.max(0, total - withReplies),
      customersCount:
        uniqueCustomers.size,
    };
  }, [sessions, totalSessionsCount]);

  // Filtered and sorted sessions
  const filteredSessions = useMemo(() => {
    let result = [...sessions];

    // Search filter
    const query =
      search.trim().toLowerCase();

    if (query) {
      result = result.filter((s) => {
        const cust = String(
          s.Customer ||
            s.phone_number ||
            ''
        ).toLowerCase();

        const sess = String(
          s.Session ||
            s.started_at ||
            ''
        ).toLowerCase();

        return (
          cust.includes(query) ||
          sess.includes(query)
        );
      });
    }

    // Reply filter
    if (replyFilter === 'replied') {
      result = result.filter(
        (s) =>
          (s.reply_count ??
            s.Replies ??
            0) > 0
      );
    } else if (
      replyFilter === 'unreplied'
    ) {
      result = result.filter(
        (s) =>
          (s.reply_count ??
            s.Replies ??
            0) === 0
      );
    }

    // Sort
    result.sort((a, b) => {
      if (
        sortBy === 'most_replies'
      ) {
        const ra =
          a.reply_count ??
          a.Replies ??
          0;

        const rb =
          b.reply_count ??
          b.Replies ??
          0;

        return rb - ra;
      }

      const timeA =
        new Date(
          a.started_at ||
            a.Session ||
            0
        ).getTime() || 0;

      const timeB =
        new Date(
          b.started_at ||
            b.Session ||
            0
        ).getTime() || 0;

      if (sortBy === 'oldest') {
        return timeA - timeB;
      }

      return timeB - timeA;
    });

    return result;
  }, [
    sessions,
    search,
    replyFilter,
    sortBy,
  ]);

  // Grouped by customer
  const groupedByCustomer =
    useMemo(() => {
      if (groupMode !== 'customer') {
        return [];
      }

      const groups = new Map();

      filteredSessions.forEach((s) => {
        const key = String(
          s.Customer ||
            s.phone_number ||
            'Unknown Customer'
        );

        if (!groups.has(key)) {
          groups.set(key, {
            customer: key,
            sessions: [],
            totalReplies: 0,
            latestActivity: '',
          });
        }

        const g = groups.get(key);

        g.sessions.push(s);

        g.totalReplies +=
          s.reply_count ??
          s.Replies ??
          0;

        if (!g.latestActivity) {
          g.latestActivity =
            s.last_activity_at ||
            s.ended_at
              ? formatSessionDate(
                  s.last_activity_at ||
                    s.ended_at
                )
              : s['Last activity']
              ? formatSessionDate(s['Last activity'])
              : '';
        }
      });

      return Array.from(
        groups.values()
      );
    }, [filteredSessions, groupMode]);

  // Grouped by date
  const groupedByDate =
    useMemo(() => {
      if (groupMode !== 'date') {
        return [];
      }

      const groups = new Map();

      filteredSessions.forEach((s) => {
        const dateKey =
          extractDateOnly(s);

        if (!groups.has(dateKey)) {
          groups.set(dateKey, {
            date: dateKey,
            sessions: [],
            totalReplies: 0,
          });
        }

        const g =
          groups.get(dateKey);

        g.sessions.push(s);

        g.totalReplies +=
          s.reply_count ??
          s.Replies ??
          0;
      });

      return Array.from(
        groups.values()
      );
    }, [filteredSessions, groupMode]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#f8fafc',
        borderLeft: '3px solid #059669',
        border: '1px solid #e2e8f0',
        borderLeftWidth: '3px',
        borderLeftColor: '#059669',
        borderRadius: '8px',
        mx: 1,
        mb: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Header & KPI Summary */}
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        justifyContent="space-between"
        gap={1}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: '#0f172a',
            }}
          >
            Sessions for {agentName}
          </Typography>

          {(() => {
            const showingCount =
              search || replyFilter !== 'all'
                ? filteredSessions.length
                : (chats ??
                   row?.['Chats'] ??
                   row?.chats ??
                   row?.chat_count ??
                   filteredSessions.length);

            const subtitle = ANALYTICS_COPY.interveneBenchmarkSessionsSubtitle
              .replace('{active}', String(showingCount))
              .replace('{total}', String(totalSessionsCount));

            return (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                }}
              >
                {subtitle}
              </Typography>
            );
          })()}
        </Box>

        {/* Quick summary chips */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ gap: 0.75 }}
        >
          <Chip
            size="small"
            icon={
              <ChatBubbleOutlineIcon
                sx={{ fontSize: 14 }}
              />
            }
            label={`Total: ${stats.total}`}
            sx={{
              fontWeight: 600,
              fontSize: '0.6875rem',
              bgcolor: '#ffffff',
              border:
                '1px solid #e2e8f0',
              height: 22,
            }}
          />

          <Chip
            size="small"
            icon={
              <PhoneIcon
                sx={{ fontSize: 14 }}
              />
            }
            label={`Customers: ${stats.customersCount}`}
            sx={{
              fontWeight: 600,
              fontSize: '0.6875rem',
              bgcolor: '#ede9fe',
              color: '#6d28d9',
              height: 22,
            }}
          />
        </Stack>
      </Stack>

      {/* Filter and Grouping Controls Bar */}
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={1}
        alignItems={{
          xs: 'stretch',
          md: 'center',
        }}
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{
          gap: 1,
          bgcolor: '#ffffff',
          p: 1,
          borderRadius: 1.5,
          border:
            '1px solid #e2e8f0',
        }}
      >
        {/* Search Input */}
        <OutlinedInput
          size="small"
          placeholder="Search customer phone..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  fontSize: 16,
                  color: '#94a3b8',
                }}
              />
            </InputAdornment>
          }
          sx={{
            minWidth: 200,
            bgcolor: '#f8fafc',
            borderRadius: 1,
            height: 32,
            fontSize: '0.75rem',
          }}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          {/* Filter by reply status */}
          <FormControl
            size="small"
            sx={{ minWidth: 130 }}
          >
            <Select
              value={replyFilter}
              onChange={(e) =>
                setReplyFilter(
                  e.target.value
                )
              }
              displayEmpty
              sx={{
                borderRadius: 1,
                height: 32,
                fontSize: '0.75rem',
                bgcolor: '#f8fafc',
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                All Sessions (
                {stats.total})
              </MenuItem>

              <MenuItem
                value="replied"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                With Replies (
                {stats.withReplies})
              </MenuItem>

              <MenuItem
                value="unreplied"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                No Replies (
                {stats.noReplies})
              </MenuItem>
            </Select>
          </FormControl>

          {/* Sort by */}
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}
          >
            <Select
              value={sortBy}
              onChange={(e) =>
                setSortBy(
                  e.target.value
                )
              }
              sx={{
                borderRadius: 1,
                height: 32,
                fontSize: '0.75rem',
                bgcolor: '#f8fafc',
              }}
            >
              <MenuItem
                value="latest"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                Newest first
              </MenuItem>

              <MenuItem
                value="oldest"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                Oldest first
              </MenuItem>

              <MenuItem
                value="most_replies"
                sx={{
                  fontSize:
                    '0.75rem',
                }}
              >
                Most replies
              </MenuItem>
            </Select>
          </FormControl>

          {/* Grouping Toggle */}
          <Box
            sx={{
              display:
                'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize:
                  '0.6875rem',
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              Group:
            </Typography>

            <ToggleButtonGroup
              size="small"
              value={groupMode}
              exclusive
              onChange={(_, next) =>
                next &&
                setGroupMode(next)
              }
              sx={{ height: 30 }}
            >
              <ToggleButton
                value="customer"
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize:
                    '0.6875rem',
                  fontWeight: 600,
                }}
              >
                Customer
              </ToggleButton>

              <ToggleButton
                value="date"
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize:
                    '0.6875rem',
                  fontWeight: 600,
                }}
              >
                Date
              </ToggleButton>

              <ToggleButton
                value="none"
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize:
                    '0.6875rem',
                  fontWeight: 600,
                }}
              >
                Flat List
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Stack>

      {/* Content Section */}
      {filteredSessions.length === 0 && isLoadingSessions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={26} />
        </Box>
      ) : sessionsError ? (
        <Alert severity="error">{sessionsError}</Alert>
      ) : filteredSessions.length === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: 'center',
            bgcolor: '#ffffff',
            borderRadius: 1,
            border:
              '1px solid #e2e8f0',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: '#64748b',
            }}
          >
            No sessions match the
            current search / filter
            criteria.
          </Typography>
        </Box>
      ) : groupMode === 'customer' ? (
        /* GROUP BY CUSTOMER */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {groupedByCustomer.map(
            (group, gIdx) => (
              <Accordion
                key={group.customer}
                defaultExpanded={
                  gIdx === 0
                }
                sx={{
                  border:
                    '1px solid #e2e8f0',
                  borderRadius:
                    '6px !important',
                  overflow: 'hidden',
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                  sx={{
                    bgcolor:
                      '#ffffff',
                    minHeight: 38,
                    py: 0.5,
                    px: 1.5,
                    '&.Mui-expanded': {
                      minHeight: 38,
                    },
                    '& .MuiAccordionSummary-content':
                      {
                        my: 0.5,
                      },
                    '&:hover': {
                      bgcolor:
                        '#f1f5f9',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      width: '100%',
                      pr: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display:
                          'flex',
                        alignItems:
                          'center',
                        gap: 1,
                      }}
                    >
                      <PhoneIcon
                        sx={{
                          fontSize: 16,
                          color:
                            '#059669',
                        }}
                      />

                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize:
                            '0.8125rem',
                          color:
                            '#0f172a',
                        }}
                      >
                        {group.customer}
                      </Typography>

                      <Chip
                        size="small"
                        label={`${group.sessions.length} ${
                          group.sessions.length ===
                          1
                            ? 'session'
                            : 'sessions'
                        }`}
                        sx={{
                          fontSize:
                            '0.6875rem',
                          fontWeight: 600,
                          bgcolor:
                            '#e0f2fe',
                          color:
                            '#0369a1',
                          height: 18,
                        }}
                      />
                    </Box>

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          fontSize:
                            '0.75rem',
                          color:
                            '#64748b',
                        }}
                      >
                        Replies:{' '}
                        <strong
                          style={{
                            color:
                              '#0f172a',
                          }}
                        >
                          {
                            group.totalReplies
                          }
                        </strong>
                      </Typography>

                      {group.latestActivity && (
                        <Typography
                          sx={{
                            fontSize:
                              '0.6875rem',
                            color:
                              '#94a3b8',
                          }}
                        >
                          Last:{' '}
                          {
                            group.latestActivity
                          }
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails
                  sx={{ p: 0 }}
                >
                  <TableContainer>
                    <Table size="small">
                      <SessionsTableHead />

                      <TableBody>
                        {group.sessions.map(
                          (sess, sIdx) => (
                            <SessionRow
                              key={
                                sess.session_id ||
                                sIdx
                              }
                              session={
                                sess
                              }
                              querySpec={querySpec}
                              orgId={orgId}
                              replyPageSize={replyPageSize}
                            />
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>
      ) : groupMode === 'date' ? (
        /* GROUP BY DATE */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {groupedByDate.map(
            (group, gIdx) => (
              <Accordion
                key={group.date}
                defaultExpanded={
                  gIdx === 0
                }
                sx={{
                  border:
                    '1px solid #e2e8f0',
                  borderRadius:
                    '6px !important',
                  overflow: 'hidden',
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                      }}
                    />
                  }
                  sx={{
                    bgcolor:
                      '#ffffff',
                    minHeight: 38,
                    py: 0.5,
                    px: 1.5,
                    '&.Mui-expanded': {
                      minHeight: 38,
                    },
                    '& .MuiAccordionSummary-content':
                      {
                        my: 0.5,
                      },
                    '&:hover': {
                      bgcolor:
                        '#f1f5f9',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      width: '100%',
                      pr: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display:
                          'flex',
                        alignItems:
                          'center',
                        gap: 1,
                      }}
                    >
                      <AccessTimeIcon
                        sx={{
                          fontSize: 16,
                          color:
                            '#3b82f6',
                        }}
                      />

                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize:
                            '0.8125rem',
                          color:
                            '#0f172a',
                        }}
                      >
                        {group.date}
                      </Typography>

                      <Chip
                        size="small"
                        label={`${group.sessions.length} sessions`}
                        sx={{
                          fontSize:
                            '0.6875rem',
                          fontWeight: 600,
                          bgcolor:
                            '#f1f5f9',
                          color:
                            '#334155',
                          height: 18,
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize:
                          '0.75rem',
                        color:
                          '#64748b',
                      }}
                    >
                      Replies:{' '}
                      <strong
                        style={{
                          color:
                            '#0f172a',
                        }}
                      >
                        {
                          group.totalReplies
                        }
                      </strong>
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails
                  sx={{ p: 0 }}
                >
                  <TableContainer>
                    <Table size="small">
                      <SessionsTableHead />

                      <TableBody>
                        {group.sessions.map(
                          (sess, sIdx) => (
                            <SessionRow
                              key={
                                sess.session_id ||
                                sIdx
                              }
                              session={
                                sess
                              }
                              querySpec={querySpec}
                              orgId={orgId}
                              replyPageSize={replyPageSize}
                            />
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>
      ) : (
        /* FLAT LIST */
        <Box
          sx={{
            border:
              '1px solid #e2e8f0',
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: '#ffffff',
          }}
        >
          <TableContainer
            sx={{ width: '100%' }}
          >
            <Table
              size="small"
              stickyHeader
            >
              <SessionsTableHead />

              <TableBody>
                {filteredSessions.map(
                  (sess, sIdx) => (
                    <SessionRow
                      key={
                        sess.session_id ||
                        sIdx
                      }
                      session={sess}
                      querySpec={querySpec}
                      orgId={orgId}
                      replyPageSize={replyPageSize}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {sessionsError ? <Alert severity="error">{sessionsError}</Alert> : null}
      {isLoadingSessions && filteredSessions.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={22} />
        </Box>
      ) : null}
      {hasMoreSessions && !search && replyFilter === 'all' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outline"
            disabled={isLoadingSessions}
            onClick={onLoadMoreSessions}
          >
            {isLoadingSessions ? 'Loading…' : 'Load more sessions'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}